// Account type definitions for the application

export enum AccountType {
  PILOT = 'pilot',
  PLANE_OWNER = 'plane_owner',
  FERRY_BROKER = 'ferry_broker',
  FERRY_PILOT_AGENCY = 'ferry_pilot_agency',
  SITE_ADMIN = 'site_admin',
  BUSINESS_EMPLOYEE = 'business_employee'
}

// Helper functions for account type logic
export const AccountTypeUtils = {
  /**
   * Check if an account type represents a business that should have a business entity created
   */
  isBusinessType: (accountType: AccountType): boolean => {
    return accountType === AccountType.FERRY_BROKER || accountType === AccountType.FERRY_PILOT_AGENCY;
  },

  /**
   * Check if an account type can post jobs
   */
  canPostJobs: (accountType: AccountType, role?: string): boolean => {
    return (
      accountType === AccountType.PLANE_OWNER ||
      accountType === AccountType.FERRY_BROKER ||
      accountType === AccountType.SITE_ADMIN ||
      // Include users with admin role in a business account
      (role === "admin" && accountType === AccountType.BUSINESS_EMPLOYEE)
    );
  },

  /**
   * Check if an account type can browse jobs
   */
  canBrowseJobs: (accountType: AccountType, role?: string): boolean => {
    return (
      (role === "employee" && accountType === AccountType.PILOT) ||
      accountType === AccountType.FERRY_PILOT_AGENCY ||
      accountType === AccountType.PLANE_OWNER ||
      accountType === AccountType.FERRY_BROKER ||
      accountType === AccountType.SITE_ADMIN ||
      // Include users with appropriate roles in a business account
      (role === "employee" && accountType === AccountType.BUSINESS_EMPLOYEE)
    );
  },

  /**
   * Check if an account type can view posted jobs
   */
  canViewPostedJobs: (accountType: AccountType, role?: string): boolean => {
    return (
      accountType === AccountType.PLANE_OWNER ||
      accountType === AccountType.FERRY_BROKER ||
      accountType === AccountType.SITE_ADMIN ||
      // Include users with appropriate roles in a business account
      (role === "admin" && accountType === AccountType.BUSINESS_EMPLOYEE) ||
      (role === "editor" && accountType === AccountType.BUSINESS_EMPLOYEE)
    );
  },

  /**
   * Check if an account type can set up job alerts
   */
  canSetupJobAlerts: (accountType: AccountType, role?: string): boolean => {
    return (
      (role === "employee" && accountType === AccountType.PILOT) ||
      accountType === AccountType.FERRY_PILOT_AGENCY ||
      // Include employees with appropriate roles in a business account
      (role === "employee" && accountType === AccountType.BUSINESS_EMPLOYEE)
    );
  },

  /**
   * Get display name for account type
   */
  getDisplayName: (accountType: AccountType): string => {
    switch (accountType) {
      case AccountType.PILOT:
        return 'Ferry Pilot';
      case AccountType.PLANE_OWNER:
        return 'Aircraft Owner';
      case AccountType.FERRY_BROKER:
        return 'Aircraft Broker';
      case AccountType.FERRY_PILOT_AGENCY:
        return 'Ferry Pilot Agency';
      case AccountType.SITE_ADMIN:
        return 'Site Administrator';
      case AccountType.BUSINESS_EMPLOYEE:
        return 'Business Employee';
      default:
        return 'Unknown';
    }
  },

  /**
   * Map old registration roles to new account types
   */
  mapRoleToAccountType: (role: string): AccountType => {
    switch (role) {
      case 'pilot':
        return AccountType.PILOT;
      case 'owner':
        return AccountType.PLANE_OWNER;
      case 'business':
        return AccountType.FERRY_BROKER;
      case 'service_provider':
        return AccountType.FERRY_PILOT_AGENCY;
      case 'site_admin':
        return AccountType.SITE_ADMIN;
      default:
        return AccountType.PILOT; // Default fallback
    }
  }
};

// Type guard to check if a string is a valid AccountType
export function isValidAccountType(value: string): value is AccountType {
  return Object.values(AccountType).includes(value as AccountType);
}

// Export the account type values as an array for validation
export const VALID_ACCOUNT_TYPES = Object.values(AccountType);
